import {
  ChevronDownIcon,
  ChevronRightIcon,
  FoldIcon,
  InfoIcon,
  UnfoldIcon,
  AlertIcon,
  FoldDownIcon,
} from '@primer/octicons-react'
import {Link, Button, Text, Flash, Box, Label} from '@primer/styled-react'
import {CounterLabel} from '@primer/react'
import {Octicon} from '@primer/styled-react/deprecated'
import type {PropsWithChildren} from 'react'
import type React from 'react'
import {useCallback, useEffect, useState} from 'react'
import {useNavigate} from '@github-ui/use-navigate'
import {encodePart} from '@github-ui/paths'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useSearchAnalytics} from '../../hooks/use-search-analytics'
import {timeSinceLastSelection, useTrackSelection} from '../../hooks/use-track-selection'
import type {
  BlackbirdResults,
  ByteRange,
  CodeResult as CodeResultType,
  SearchResponse,
  Snippet,
} from '../../types/blackbird-types'
import EllipsisOverflow from '../EllipsisOverflow'
import LanguageCircle from '../LanguageCircle'
import Result from '../search-result'
import MonaSearchImage from '../NoResults/MonaSearchImage'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {debugScoringInfoEnabled} from '../../../../../components/search/experiments'

// The number of snippets to render by default
const NUM_SNIPPETS_COLLAPSED = 8

// The backend requests a max of 5 locations (which means a max of 4 duplicates) but when the there are very few
// documents matching the query, blackbird bump the limits and potentially returns many more locations.
//
// See:
// - https://github.com/github/github/blob/679ce091fe5be4905e74098ef92d82986f6c16ab/app/controllers/blackbird_controller_methods.rb#L454
// - https://github.com/github/blackbird-mw/blob/ad0dcca3d52b316bea2987ed4e50077635ebd733/internal/search/blackbird/search.go#L118
const MAX_DUPLICATE_LOCATIONS = 4

export default function Code({
  results,
  isRepoSearch,
}: {
  results: BlackbirdResults & SearchResponse
  isRepoSearch: boolean
}) {
  // Track the user's last interaction with text selection, so that we can
  // accommodate copying parts of result snippets while also allowing navigation
  // when users directly click on a result.
  useTrackSelection()

  const [duplicatesExpanded, setDuplicatesExpanded] = useState(true)

  const searchResults = results.results || []
  const numDuplicates = searchResults.reduce((acc, r) => acc + r.duplicate_locations.length, 0)
  const hitLimit = results.metadata?.limit_reached || false

  const {sendSampledSearchResult} = useSearchAnalytics()
  useEffect(() => {
    if (results.results) {
      // Randomly select one of the results to sample impressions of
      const position = Math.floor(Math.random() * results.results.length)
      const sampledResult = results.results[position]
      if (sampledResult) {
        sendSampledSearchResult(results.query_id, position, sampledResult)
      }
    }
  }, [results, sendSampledSearchResult])

  // workaround for tsc complaining about the href prop being set when as: 'button' is set
  // as this does not match the semantics of a button
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const linkProps = {href: '#'} as any

  return results.logged_in ? (
    <Result.List sx={{gap: 3}}>
      {numDuplicates > 0 && (
        <Box
          sx={{
            pb: 1,
            fontSize: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            mx: 'auto',
          }}
        >
          <Text sx={{color: 'fg.muted'}}>
            <Octicon icon={InfoIcon} size={16} sx={{mr: 2}} />
            {duplicatesExpanded
              ? 'Files with identical content are grouped together. '
              : `Hiding ${numDuplicates} ${pluralize(numDuplicates, 'file', 'files')} with identical content. `}
            <Link inline as="button" onClick={() => setDuplicatesExpanded(!duplicatesExpanded)} {...linkProps}>
              {duplicatesExpanded ? 'Hide these extra files instead' : 'Show all files'}
            </Link>
          </Text>
        </Box>
      )}
      {searchResults?.map((item, i) => (
        <CodeResult
          key={`cs-result-${item.repo_nwo}-${item.path}`}
          item={item}
          position={i}
          queryId={results.query_id}
          shouldRenderRepo={!isRepoSearch}
          shouldRenderDuplicates={duplicatesExpanded}
          hitLimit={hitLimit}
        />
      ))}
    </Result.List>
  ) : (
    <Box
      sx={{display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center', justifyContent: 'center'}}
    >
      <Box sx={{maxWidth: '400px'}}>
        <MonaSearchImage />
      </Box>
      <Text sx={{fontWeight: 'bold', fontSize: 4, mt: 3}}>Sign in to search code on GitHub</Text>
      <Text sx={{color: 'fg.muted', maxWidth: '400px', textAlign: 'center', mt: 2}}>
        Before you can access our code search functionality please sign in or create a free account.
      </Text>
      <Box sx={{display: 'flex', mt: 3}}>
        <Button as="a" href={results.sign_in_path}>
          Sign in
        </Button>
        {results.sign_up_path && (
          <Button as="a" href={results.sign_up_path} sx={{ml: 2}} variant="primary">
            Sign up
          </Button>
        )}
      </Box>
    </Box>
  )
}

function shouldUsePlainRepresentation(languageId: number) {
  // NOTE: Specially renderable filetypes come from here:
  // https://github.com/github/viewscreen#renderables-supported-so-far
  const specialRenderedLanguageIds = [
    222, // Markdown
    337, // SVG
  ]
  return specialRenderedLanguageIds.includes(languageId)
}

function CodeResult({
  item,
  queryId,
  position,
  shouldRenderRepo,
  shouldRenderDuplicates,
  isDuplicate,
  isLastDuplicate,
  hitLimit,
}: {
  item: CodeResultType
  queryId: string
  position: number
  shouldRenderRepo: boolean
  shouldRenderDuplicates: boolean
  isDuplicate?: boolean
  isLastDuplicate?: boolean
  hitLimit: boolean
}) {
  const [isOpen, setIsOpen] = useState(!isDuplicate)
  const [shouldShowDuplicates, setShouldShowDuplicates] = useState(shouldRenderDuplicates)

  const filePath = item.path
  const owner = item.repo_nwo.split('/')[0]!
  const repoName = item.repo_nwo.split('/')[1]!
  const repositoryOwnerAvatarUrl = `/${owner}.png`
  const repositoryUrl = `/${item.repo_nwo}`
  const plainSuffix = shouldUsePlainRepresentation(item.language_id) ? '?plain=1' : ''
  const htmlUrl = `${repositoryUrl}/blob/${item.commit_sha}/${encodePart(filePath)}${plainSuffix}`

  const hasGoodAndBadSnippets = item.snippets.some(i => i.score >= 0) && item.snippets.some(i => i.score < 0)
  const hasMoreSnippets = item.snippets.length > NUM_SNIPPETS_COLLAPSED || hasGoodAndBadSnippets
  const [collapsed, setCollapsed] = useState(hasMoreSnippets)

  const visibleSnippets = collapsed
    ? item.snippets.filter(s => (hasGoodAndBadSnippets ? s.score >= 0 : true)).slice(0, NUM_SNIPPETS_COLLAPSED)
    : item.snippets
  const visibleMatchCount = visibleSnippets.reduce((acc, s) => acc + s.match_count, 0)
  const totalMatchCount = item.match_count
  const reachedLimit = hasMoreSnippets && totalMatchCount - visibleMatchCount > 0 && !collapsed
  const {sendSearchResultClick} = useSearchAnalytics()
  const clickAnalyticsCallback = useCallback(() => {
    sendSearchResultClick(queryId, position, item)
  }, [sendSearchResultClick, queryId, position, item])

  useEffect(() => {
    setShouldShowDuplicates(shouldRenderDuplicates)
  }, [shouldRenderDuplicates])

  const hasVisibleDuplicates = item.duplicate_locations.length > 0 && shouldShowDuplicates
  const showMorePathsButton = !shouldShowDuplicates && item.duplicate_locations.length > 0 && !shouldRenderDuplicates
  const isHeaderLastBox = !isOpen && !hasVisibleDuplicates && (!isDuplicate || isLastDuplicate) && !showMorePathsButton
  const isLastBox =
    (isDuplicate && isLastDuplicate && !hasMoreSnippets && !showMorePathsButton) ||
    (!isDuplicate && !hasVisibleDuplicates && !hasMoreSnippets && !showMorePathsButton)
  const isSnippetLastBox = !hasVisibleDuplicates && (!isDuplicate || isLastDuplicate) && !showMorePathsButton

  // The default URL should link to the line number of the first visible snippet, which
  // can change if the snippets are expanded.
  let lineNumberSuffix = item.line_number > 1 ? `#L${item.line_number}` : ''
  if (visibleSnippets.length > 0) {
    const firstSnippet = visibleSnippets[0]!
    const lineNumber = Math.floor((firstSnippet.ending_line_number + firstSnippet.starting_line_number) / 2)
    lineNumberSuffix = lineNumber > 1 ? `#L${lineNumber}` : ''
  }
  const defaultUrl = `${htmlUrl}${lineNumberSuffix}`

  const showDebugScoringInfo = debugScoringInfoEnabled()

  return (
    <Box sx={{minWidth: 0}}>
      <Box
        className="notranslate"
        sx={{
          bg: 'canvas.subtle',
          pl: 2,
          pr: 3,
          height: 40,
          listStyle: 'none',
          borderTopLeftRadius: isDuplicate ? 0 : 2,
          borderTopRightRadius: isDuplicate ? 0 : 2,
          borderBottomRightRadius: isHeaderLastBox ? 2 : 0,
          borderBottomLeftRadius: isHeaderLastBox ? 2 : 0,
          borderColor: 'border.default',
          borderWidth: 1,
          borderStyle: 'solid',
          borderBottomWidth: isDuplicate && !isHeaderLastBox && !isOpen ? 0 : 1,
          borderBottomColor: 'border.muted',
          borderTopColor: 'border.muted',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <OpenCloseButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} name={`${item.repo_nwo} · ${filePath}`} />
        <Box sx={{flex: 1, alignItems: 'center', display: 'flex', ml: 1, minWidth: 0}}>
          <Result.Avatar mr={2}>
            <GitHubAvatar square sx={{ml: '1px'}} size={16} src={repositoryOwnerAvatarUrl} />
          </Result.Avatar>
          <Result.Title fontSize={1}>
            <EllipsisOverflow side="left" title={`${item.repo_nwo} · ${filePath}`}>
              {shouldRenderRepo && (
                <>
                  <Result.RepositoryLink
                    owner={owner}
                    name={repoName}
                    style={{
                      color: 'var(--fgColor-default)',
                      fontWeight: 'bold',
                      direction: 'ltr',
                      unicodeBidi: 'bidi-override',
                    }}
                  />
                  {item.repo_is_archived && (
                    <Label sx={{ml: 1}} variant="attention">
                      Archived
                    </Label>
                  )}
                  &nbsp;·&nbsp;
                </>
              )}
              <Link
                href={defaultUrl}
                sx={{color: 'fg.default', fontWeight: 'normal', direction: 'ltr', unicodeBidi: 'bidi-override'}}
                data-testid="link-to-search-result"
              >
                <FilePath filePath={filePath} matches={item.path_term_matches || []} />
              </Link>
            </EllipsisOverflow>
            {showDebugScoringInfo && (
              <Box sx={{pl: 2}}>{`score=${item.debug_info?.score.toFixed(2)} rpos=${
                item.debug_info?.retrieval_position
              }`}</Box>
            )}
          </Result.Title>
        </Box>
        <Box sx={{flexShrink: 0, pl: 4, display: ['none', 'none', 'flex']}}>
          <Result.Footer sx={{mt: 0}}>
            {!isDuplicate && item.has_language_id && (
              <Result.FooterItem>
                <IconWithText ariaLabel={`${item.language_name} language`} text={item.language_name}>
                  <Box sx={{mr: 2}}>
                    <LanguageCircle color={item.language_color} />
                  </Box>
                </IconWithText>
              </Result.FooterItem>
            )}
            {!isDuplicate && (
              <Result.FooterItem>
                <CounterLabel>{item.match_count}</CounterLabel>
              </Result.FooterItem>
            )}
          </Result.Footer>
        </Box>
      </Box>

      {isOpen && (
        <>
          <Box
            sx={{
              borderColor: 'border.default',
              borderBottomWidth: isLastBox ? 1 : 0,
              borderBottomLeftRadius: isLastBox ? 2 : 0,
              borderBottomRightRadius: isLastBox ? 2 : 0,
              borderTopWidth: 0,
              borderLeftWidth: 1,
              borderRightWidth: 1,
              borderStyle: 'solid',
            }}
          >
            <MatchSnippet
              defaultUrl={defaultUrl}
              url={htmlUrl}
              snippets={visibleSnippets}
              clickAnalyticsCallback={clickAnalyticsCallback}
            />
          </Box>

          {reachedLimit && (
            <Flash variant="warning" sx={{fontSize: 0, borderRadius: 0, py: 2, px: 3}}>
              This file contains {totalMatchCount - visibleMatchCount} more{' '}
              {totalMatchCount - visibleMatchCount === 1 ? 'match' : 'matches'} not shown.{' '}
              {totalMatchCount === 1 ? (
                <Link href={defaultUrl}>See all matches in the full file</Link>
              ) : (
                <Link href={defaultUrl}>See all {totalMatchCount} matches in the full file</Link>
              )}
            </Flash>
          )}
          {hasMoreSnippets && (
            <Link
              onClick={e => {
                e.preventDefault()
                setCollapsed(!collapsed)
              }}
              href="#"
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 2,
                bg: 'canvas.default',
                px: 3,
                height: 40,
                color: 'fg.muted',
                borderColor: 'border.default',
                borderWidth: 1,
                borderStyle: 'solid',
                borderTopStyle: 'dashed',
                borderTopColor: 'border.muted',
                borderTopWidth: reachedLimit ? 0 : 1,
                borderBottomWidth: isSnippetLastBox ? 1 : 0,
                borderBottomRightRadius: isSnippetLastBox ? 2 : 0,
                borderBottomLeftRadius: isSnippetLastBox ? 2 : 0,
                fontSize: 0,
                ':hover': {
                  bg: 'canvas.subtle',
                },
              }}
            >
              <Octicon
                icon={collapsed ? UnfoldIcon : FoldIcon}
                size={16}
                sx={{
                  mr: 2,
                }}
              />
              {collapsed
                ? `Show ${totalMatchCount - visibleMatchCount} more match${
                    totalMatchCount - visibleMatchCount === 1 ? '' : 'es'
                  }`
                : `Show less`}
            </Link>
          )}
        </>
      )}

      {showMorePathsButton && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderColor: 'border.default',
            borderWidth: 1,
            borderBottomRightRadius: 2,
            borderBottomLeftRadius: 2,
            borderStyle: 'solid',
          }}
        >
          <Button
            variant="invisible"
            size="small"
            alignContent="start"
            leadingVisual={FoldDownIcon}
            sx={{
              py: 0,
              px: 3,
              width: '100%',
              fontSize: 0,
              height: 40,
              color: 'fg.muted',
              borderBottomRightRadius: 2,
              borderBottomLeftRadius: 2,
              borderRadius: 0,
            }}
            onClick={() => setShouldShowDuplicates(!shouldShowDuplicates)}
          >
            +{item.duplicate_locations.length} more {pluralize(item.duplicate_locations.length, 'file', 'files')} with
            the same content
          </Button>
        </Box>
      )}

      {hasVisibleDuplicates &&
        item.duplicate_locations.map((loc, i) => {
          return (
            <CodeResult
              key={`dup-${loc.repo_nwo}-${loc.path}`}
              item={Object.assign({}, item, loc, {duplicate_locations: []})}
              position={position}
              queryId={queryId}
              shouldRenderRepo={shouldRenderRepo}
              shouldRenderDuplicates={false}
              isDuplicate
              isLastDuplicate={i === item.duplicate_locations.length - 1}
              hitLimit={hitLimit}
            />
          )
        })}

      {hasVisibleDuplicates && item.duplicate_locations.length === MAX_DUPLICATE_LOCATIONS && hitLimit && (
        <Box
          sx={{
            fontSize: 0,
            px: 2,
            py: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            mx: 'auto',
          }}
        >
          <Text sx={{color: 'fg.muted'}}>
            <Octicon icon={AlertIcon} size={16} sx={{mr: 2}} />
            Only showing a subset of file paths with this content. Refine your search to see more.
          </Text>
        </Box>
      )}
    </Box>
  )
}

const encoder = new TextEncoder()
const utf8Decoder = new TextDecoder('utf-8')

export function FilePath({filePath, matches}: {filePath: string; matches: ByteRange[]}) {
  if (matches.length === 0) {
    return filePath
  }

  const bFilePath = encoder.encode(filePath)
  const path = []
  let cursor = 0
  let markEnd = 0

  for (const m of matches) {
    if (m.start <= markEnd) {
      // Overlapping or adjacent match - extend the current mark region
      markEnd = Math.max(markEnd, m.end)
    } else {
      // Non-overlapping match - flush any pending mark first
      if (markEnd > cursor) {
        path.push(<mark key={`pm-${cursor}`}>{utf8Decoder.decode(bFilePath.slice(cursor, markEnd))}</mark>)
        cursor = markEnd
      }
      // Add the gap before this match
      if (m.start > cursor) {
        path.push(<span key={`p-${m.start}`}>{utf8Decoder.decode(bFilePath.slice(cursor, m.start))}</span>)
        cursor = m.start
      }
      // Start new mark region
      markEnd = m.end
    }
  }

  // Flush any remaining mark
  if (markEnd > cursor) {
    path.push(<mark key={`pm-${cursor}`}>{utf8Decoder.decode(bFilePath.slice(cursor, markEnd))}</mark>)
    cursor = markEnd
  }

  // Add any remaining text after the last match
  if (cursor < bFilePath.length) {
    path.push(<span key="end">{utf8Decoder.decode(bFilePath.slice(cursor))}</span>)
  }

  return (
    <Text
      as="span"
      sx={{
        mark: {
          backgroundColor: 'var(--highlight-neutral-bgColor, var(--color-search-keyword-hl))',
          color: 'var(--fgColor-default, var(--color-fg-default))',
        },
      }}
    >
      {path}
    </Text>
  )
}

function OpenCloseButton({isOpen, onClick, name}: {isOpen: boolean; onClick: () => void; name: string}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      aria-label={isOpen ? `Collapse ${name}` : `Expand ${name}`}
      sx={{
        height: 24,
        borderRadius: 2,
        width: 24,
        color: 'fg.muted',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bg: 'canvas.subtle',
        border: 'none',
        ':hover': {
          bg: 'btn.hoverBg',
        },
      }}
    >
      {isOpen ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
    </Box>
  )
}

function MatchSnippet({
  url,
  defaultUrl,
  snippets,
  clickAnalyticsCallback,
}: {
  url: string | undefined
  snippets: Snippet[]
  defaultUrl: string
  clickAnalyticsCallback: () => void
}) {
  const nav = useNavigate()

  const snippetElements = []
  let nextLineNumber: number | undefined = undefined
  let lineNumberAlternation = false
  for (const snippet of snippets) {
    if (nextLineNumber !== undefined && nextLineNumber !== snippet.starting_line_number) {
      lineNumberAlternation = !lineNumberAlternation
      snippetElements.push(
        <tr key={`divider-${snippetElements.length}`} role="row">
          <td colSpan={2} role="cell">
            <div style={{marginTop: 4, marginBottom: 7}} />
          </td>
        </tr>,
      )
    }
    nextLineNumber = snippet.starting_line_number + snippet.lines.length

    snippetElements.push(
      snippet.lines.map((line, i) => {
        const lineNumber = i + snippet.starting_line_number
        const fileLink = `${url}#L${lineNumber}`

        const navigateToSnippet = (e: React.MouseEvent) => {
          if (!window.getSelection()?.isCollapsed) {
            e.stopPropagation()
            return
          }

          // Allow users to select and manipulate their selection without
          // triggering navigation
          if (timeSinceLastSelection() < 100) {
            e.stopPropagation()
            return
          }

          clickAnalyticsCallback()

          // NOTE: I have to disable these checks bc this has nothing to do with hotkeys
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          if (e.ctrlKey || e.metaKey) {
            window.open(fileLink, '_blank')
          } else {
            nav(fileLink)
          }

          e.stopPropagation()
        }

        return (
          <tr key={fileLink} onClick={navigateToSnippet} role="row">
            <td className="blob-num" role="cell">
              <Link onClick={e => e.stopPropagation()} href={fileLink}>
                <Text sx={{color: lineNumberAlternation ? 'fg.default' : 'ft.muted'}}>{lineNumber}</Text>
              </Link>
            </td>
            <td className="blob-code blob-code-inner" role="cell">
              {snippet.format === 'SNIPPET_FORMAT_HTML' ? (
                <VerifiedHTMLText
                  as="span"
                  html={snippet.lines[i]}
                  sx={{
                    cursor: 'text',
                    wordWrap: 'break-word',
                    color: 'fg.default',
                    overflowWrap: 'anywhere',
                    mark: {
                      backgroundColor: 'var(--highlight-neutral-bgColor, var(--color-search-keyword-hl))',
                      color: 'var(--fgColor-default, var(--color-fg-default))',
                    },
                  }}
                />
              ) : (
                <Text
                  as="span"
                  sx={{
                    cursor: 'text',
                    wordWrap: 'break-word',
                    color: 'fg.default',
                    overflowWrap: 'anywhere',
                    mark: {
                      backgroundColor: 'var(--highlight-neutral-bgColor, var(--color-search-keyword-hl))',
                      color: 'var(--fgColor-default, var(--color-fg-default))',
                    },
                  }}
                >
                  {line}
                </Text>
              )}
            </td>
          </tr>
        )
      }),
    )
  }

  return (
    <Box
      // 'notranslate' is to tell Google translate to not bother
      className="code-list notranslate"
      sx={{
        whiteSpace: 'pre-wrap',
        fontFamily: 'monospace',
        minWidth: 0,
        cursor: 'pointer',
        py: 2,
      }}
      onClick={(e: React.MouseEvent) => {
        // Allow users to select and manipulate their selection without
        // triggering navigation
        if (timeSinceLastSelection() < 100) {
          return
        }
        // Only trigger a navigation if the user isn't selecting something
        if (window.getSelection()?.isCollapsed) {
          clickAnalyticsCallback()

          // Open in a new tab if ctrl or cmd key are pressed
          // NOTE: I have to disable these checks bc this has nothing to do with hotkeys
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          if (e.ctrlKey || e.metaKey) {
            window.open(defaultUrl, '_blank')
          } else {
            nav(defaultUrl)
          }
        }
      }}
    >
      <table role="table">
        {
          // We need to add role="rowgroup" because we don't have table headers
          // And in order to screen readers to know that we really mean for this to be a table
          // We must define it explicitly
          <tbody role="rowgroup">{snippetElements}</tbody>
        }
      </table>
    </Box>
  )
}

interface IconWithTextProps extends PropsWithChildren {
  ariaLabel: string
  text: string
  textUrl?: string
}

function IconWithText({ariaLabel, text, textUrl, children}: IconWithTextProps) {
  const inner = (
    <Box sx={{display: 'flex', alignItems: 'center'}}>
      {children}
      {/* eslint-disable-next-line github/a11y-role-supports-aria-props */}
      <span aria-label={ariaLabel}>{text}</span>
    </Box>
  )

  if (!textUrl) return inner

  return (
    <Link href={textUrl} sx={{color: 'fg.muted'}}>
      {inner}
    </Link>
  )
}

function pluralize(count: number, singular: string, plural: string) {
  return count === 1 ? singular : plural
}
